import {Spin, Button, Card, Space, Tag, Typography} from 'antd'
import {Link, Navigate, useLocation} from 'react-router-dom'
import useSubscription from '@hooks/useSubscription.js'
import usePlans from '@hooks/usePlans.js'
import {deriveSubscriptionAccess, getSubscriptionAccessEnd} from '../../subscription/runtime.js'

const {Text, Title} = Typography
const FRIEND_INVITE_STORAGE_KEY = 'friendsInviteCode'

function getFriendInviteCode() {
  if (typeof window === 'undefined') return ''
  try {
    return localStorage.getItem(FRIEND_INVITE_STORAGE_KEY)?.trim() || ''
  } catch {
    return ''
  }
}

function formatPrice(p) {
  if (!p?.unit_amount || !p?.currency) return ''
  const amount = p.unit_amount / 100
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: p.currency.toUpperCase()
  }).format(amount)
}

function CurrentPlan({plan, status}) {
  if (!plan) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: '2rem'}}>
        <Card>
          <p style={{marginBottom: '1rem'}}>No active subscription</p>
          <Link to="/subscribe">
            <Button type="primary">Choose a Plan</Button>
          </Link>
        </Card>
      </div>
    )
  }
  return (
    <div style={{display: 'flex', justifyContent: 'center', padding: '2rem'}}>
      <Card style={{maxWidth: 440}}>
        <Space direction="vertical" size="small" style={{width: '100%'}}>
          <Space align="center" size="small">
            <Title level={4} style={{margin: 0}}>{plan.title || plan.id}</Title>
            <Tag color="blue">{plan.slug || plan.id}</Tag>
          </Space>
          {status === 'grace' && (
            <Text type="danger">Subscription expired and is in grace period</Text>
          )}
          {plan.headline && <Text>{plan.headline}</Text>}
          {plan.description && <Text type="secondary">{plan.description}</Text>}
          {Array.isArray(plan.featureBullets) && plan.featureBullets.length > 0 && (
            <ul style={{paddingLeft: 20, marginBottom: 0, color: 'var(--ant-color-text-secondary)'}}>
              {plan.featureBullets.map((bullet, index) => (
                <li key={`${plan.id}-feature-${index}`}>{bullet}</li>
              ))}
            </ul>
          )}
          {typeof plan.aiCreditsPerPeriod === 'number' && plan.aiCreditsPerPeriod > 0 && (
            <Text type="secondary">
              {plan.aiCreditsPerPeriod} AI credits / period
            </Text>
          )}
          {plan.unit_amount && plan.currency && (
            <Text strong>Price: {formatPrice(plan)}</Text>
          )}
          <Link to="/subscribe">
            <Button type="primary">Change Plan</Button>
          </Link>
        </Space>
      </Card>
    </div>
  )
}

export default function RequireSubscription({children, plan = 'basic', country}) {
  const subscription = useSubscription()
  const plans = usePlans(country)
  const location = useLocation()

  if (!subscription && !plans) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: '2rem'}}>
        <Spin />
      </div>
    )
  }

  const accessEnd = getSubscriptionAccessEnd(subscription)

  if (
    plan === 'full' &&
    subscription.plan === 'friends' &&
    subscription.status === 'active' &&
    accessEnd
  ) {
    return children
  }

  const {allowed} = deriveSubscriptionAccess({
    subscription,
    plans: plans || [],
    requiredPlan: plan
  })
  const planMap = (plans || []).reduce((acc, p) => {acc[p.id] = p; return acc}, {})

  if (!allowed) {
    const friendInviteCode = getFriendInviteCode()
    if (friendInviteCode) {
      return <Navigate to={`/subscribe/friends?code=${encodeURIComponent(friendInviteCode)}`} replace />
    }
    if (subscription.plan === 'free' || subscription.status !== 'active') {
      return <Navigate to="/subscribe" replace state={{from: location}} />
    }
    return <CurrentPlan plan={planMap[subscription.plan]} status={subscription.status} />
  }

  return children
}
